import { Trans } from '@lingui/macro'
import {
  Box,
  Grid,
  Typography
} from '@mui/material'
import { dateFormat } from 'app/appSettings'
import { bilingualDateFormat } from 'app/views/common/Formats'
import { FieldArray } from 'formik'
import React from 'react'
import { useSelector } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import './FormMilestoneUpdate.css'

/**
 * Form element which allows adding and configuring project milestone updates.
 * Milestone Updates are saved as Benchmark_Update__c child objects on connected Grantee Report object.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection} connectedObject Connected Salesforce object of type Opportunity.
 * @param  {Object} connectedMap Map of all connected objects.
 */
export const FormMilestoneUpdatePrint = ({
  id,
  useMultiuser,
  muBag,
  title,
  helpText,
  connectedObject,
  editMode,
  disabled,
  typeProps,
  describeMap,
  langVersion,
  i18n,
  connectedMap,
  ...props
}) => {
  const language = useSelector((state) => state.user.language) || 'en'

  const granteeReportConnectedId = typeProps.connectedTo?.find(
    (item) =>
      connectedMap?.[item?.connectedObject]?.objectType ===
      'FGM_Base__Grantee_Report__c'
  )?.connectedObject

  const lastReportTitle = (
    <Trans>FORM_MILESTONE_UPDATE_LAST_REPORT_SUBTITLE</Trans>
  )
  const previousReportTitle = (
    <Trans>FORM_MILESTONE_UPDATE_PREVIOUS_REPORT_SUBTITLE</Trans>
  )

  const invalid = Boolean(
    !connectedObject ||
      !connectedObject.Id ||
      connectedObject.attributes.type !== 'Opportunity'
  )
  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>
          There is no object connected in editor or main connected object is not
          of "Opportunity" type!
        </Trans>
      </div>
    )
  }

  if (!granteeReportConnectedId && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        <Trans>FORM_MILESTONE_UPDATE_GRANTEE_REPORT_CONNECTION_ERROR</Trans>
      </div>
    )
  }

  return (
    <FieldArray
      name={id}
      render={({ push, form, name, remove, meta }) => {
        const milestones = form.values[name] || []

        return (
          <div className='form-update-print body'>
            {milestones.map((item, index) => {
              const startDate = !item.startDate
                ? ''
                : typeof item.startDate === 'string'
                  ? item.startDate
                  : item.startDate.startOf('day').format(dateFormat)
              const endDate = !item.endDate
                ? ''
                : typeof item.endDate === 'string'
                  ? item.endDate
                  : item.endDate.startOf('day').format(dateFormat)
              return (
                <React.Fragment key={item.id}>
                  <Box className='form-update-print body section'>
                    <Box className='stage-title'>
                      {index === 0 && (
                        <Typography className='title'>
                          <Trans>FORM_MILESTONE_UPDATE_VIEW_ONLY_TITLE</Trans>
                        </Typography>
                      )}
                      <div
                        className={`title-section ${item.id}`}
                        {...(item.id ? { id: item.id } : {})}
                      >
                        <Typography className='subtitle'>{myI18n._(item.stage)}</Typography>
                      </div>
                    </Box>

                    <div className='block'>
                      <Grid item container xs direction='row'>
                        <Grid
                          item
                          container
                          direction='row'
                        >
                          <div style={{ display: 'flex', gap: '4px', marginRight: '16px' }}>
                            <Typography className='small-label'>
                              <Trans>Start Date</Trans>:
                            </Typography>
                            <Typography className='small-black-bold'>{startDate}</Typography>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <Typography className='small-label'>
                              <Trans>End Date</Trans>:{' '}
                            </Typography>
                            <Typography className='small-black-bold'>{endDate}</Typography>
                          </div>
                        </Grid>
                      </Grid>
                      <Grid item xs>
                        <Grid
                          container
                          wrap='nowrap'
                        >
                          <Grid
                            item
                            xs
                          >
                            <Typography className='small-blue-label'>
                              <Trans>Start date</Trans>
                            </Typography>
                            <Typography className='normal-text'>
                              {item.milestoneUpdate?.phaseStartDate}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs
                          >
                            <Typography className='small-blue-label'>
                              <Trans>End date</Trans>
                            </Typography>
                            <Typography className='normal-text'>
                              {item.milestoneUpdate?.phaseEndDate}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </div>

                    <div className='block'>
                      <Grid item xs={12}>
                        <Grid container direction='column' spacing={3}>

                          <Grid item>
                            <Typography className='medium-black-bold'>
                              <Trans>
                                FORM_MILESTONE_UPDATE_PRIMARY_ACTIVITIES_TITLE
                              </Trans>
                            </Typography>

                            <Typography className='light-text'>
                              {item.primaryActivities}
                            </Typography>
                          </Grid>

                          <Grid item>
                            {item.reportMilestoneUpdates.map((answer, idx) => {
                              const title =
                                idx === 0 ? lastReportTitle : previousReportTitle

                              const formattedDate = bilingualDateFormat(
                                answer.submissionDate,
                                language
                              )

                              return (
                                <Grid container direction='column' spacing={1} key={idx}>
                                  <Grid item>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Typography className='small-label'>
                                        {title}:
                                      </Typography>
                                      <Typography className='small-black-bold'>
                                        {formattedDate}
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid item>
                                    <Typography className='light-text'>
                                      {answer?.primaryActivities}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              )
                            })}
                          </Grid>

                          <Grid item>
                            <Typography className='medium-black-bold'>
                              <Trans>
                                FORM_MILESTONE_UPDATE_COMMENTS_TITLE
                              </Trans>
                            </Typography>

                            <Typography className='light-text'>
                              {item.comments}
                            </Typography>
                          </Grid>

                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography className='small-blue-label'>
                          <Trans>
                            FORM_MILESTONE_UPDATE_PRIMARY_ACTIVITIES_QUESTION
                          </Trans>
                        </Typography>

                        <Typography className='normal-text'>
                          {item.milestoneUpdate?.primaryActivities}
                        </Typography>
                      </Grid>
                    </div>

                    <div className='block'>
                      <Grid container xs={6} direction='column'>
                        <div style={{ marginBottom: '32px' }}>
                          <Typography className='medium-black-bold'>
                            <Trans>
                              FORM_MILESTONE_UPDATE_IMPLEMENTED_ACTIVITIES_TITLE
                            </Trans>
                          </Typography>
                        </div>
                        <div>
                          {item.reportMilestoneUpdates.map(
                            (answer, idx) => {
                              const title =
                                idx === 0
                                  ? lastReportTitle
                                  : previousReportTitle
                              const bilingualDate = bilingualDateFormat(
                                answer.submissionDate,
                                language
                              )
                              return (
                                <Box key={idx} sx={{ display: 'flex', gap: 0.5, marginBottom: '8px' }}>
                                  <Typography className='small-label'>
                                    {title}:
                                  </Typography>
                                  <Typography className='small-black-bold'>
                                    {bilingualDate}
                                  </Typography>
                                </Box>
                              )
                            }
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography className='small-blue-label'>
                          <Trans>
                            FORM_MILESTONE_UPDATE_IMPLEMENTED_ACTIVITIES_QUESTION
                          </Trans>
                        </Typography>
                        <Typography className='normal-text'>
                          {item.milestoneUpdate?.implementedActivities}
                        </Typography>
                      </Grid>
                    </div>
                  </Box>
                  {(index !== milestones.length - 1) &&
                    <div
                      style={{
                        height: '8px',
                        width: '100%',
                        backgroundColor: '#EEEEE657'
                      }}
                    />}
                </React.Fragment>
              )
            })}
          </div>
        )
      }}
    />
  )
}
